<template>
  <v-container fluid class="ps-manage-customers-page">
    <!-- Summary Cards (4 Cards) -->
    <KpiCards class="mb-4" :items="kpiItems" :loading="loadingSummary" :skeleton="4" />

    <v-card flat class="rounded-xl main-customers-card">
      <v-card-title class="pa-4 pa-sm-5 pb-3">
        <div
          class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center w-100"
          style="gap: 16px"
        >
          <h2 class="font-weight-bold text-h5 mb-0 page-title">{{ $t("customer") }}</h2>
          <div
            class="d-flex flex-column flex-sm-row align-start align-sm-center"
            style="gap: 12px; flex: 0 0 auto"
          >
            <div style="width: 100%; max-width: 300px">
              <TextSearch
                v-model="search"
                @search="fetchItem()"
                @onClear="
                  search = '';
                  fetchItem();
                "
              ></TextSearch>
            </div>
            <v-btn
              depressed
              color="primary"
              @click="newItem"
              class="white--text btn-primary-brand rounded-lg font-weight-bold px-4"
              :small="$vuetify.breakpoint.xs"
            >
              <v-icon left small>fal fa-plus</v-icon>
              {{ $t("new_customer") }}
            </v-btn>
          </div>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4 pa-sm-5">
        <div v-if="listLoading" class="text-center py-5">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>

        <v-alert
          v-else-if="listError"
          type="error"
          dense
          outlined
          class="mb-0"
        >
          {{ $t("error_occurred") }}
          <template v-slot:append>
            <v-btn text small color="primary" @click="fetchItem">
              {{ $t("try_again") }}
            </v-btn>
          </template>
        </v-alert>

        <div
          v-else-if="!customerRows.length"
          class="text-center py-8 grey--text text-body-2"
        >
          {{ $t("no_data") }}
        </div>

        <div v-else>
          <div
            v-for="(item, index) in customerRows"
            :key="item.id || index"
            class="mb-3"
          >
            <v-card
              outlined
              flat
              class="rounded-xl customer-card"
              @click="viewItem(item)"
            >
                  <div class="customer-card__inner">
                    <!-- Left: Identity -->
                    <div class="customer-card__identity">
                      <v-avatar
                        size="44"
                        :color="getColor(index)"
                        class="customer-card__avatar"
                      >
                        <span class="white--text font-weight-bold">{{
                          initials(item.first_name)
                        }}</span>
                      </v-avatar>
                      <div class="customer-card__identity-text">
                        <div class="customer-card__name">
                          {{ item.first_name || "-" }}
                        </div>
                        <div class="customer-card__meta">
                          <span class="customer-card__meta-item">
                            <v-icon x-small class="mr-1">fal fa-phone</v-icon>
                            {{ item.phone_no || "-" }}
                          </span>
                          <span
                            v-if="item.email"
                            class="customer-card__meta-item"
                          >
                            <v-icon x-small class="mr-1"
                              >fal fa-envelope</v-icon
                            >
                            {{ item.email }}
                          </span>
                          <span class="customer-card__meta-id">
                            ID #{{ item.id }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Center: Stat strip -->
                    <div class="customer-card__stats">
                      <div class="stat-cell">
                        <div class="stat-cell__label">
                          {{ $t("loyalty_points") }}
                        </div>
                        <div class="stat-cell__value">
                          {{ (item.point || 0) | ToNumber }}
                          <span class="stat-cell__unit">pts</span>
                        </div>
                      </div>

                      <div v-if="isStampFeatureEnabled" class="stat-cell">
                        <div class="stat-cell__label">
                          {{ $t("stamp_progress") }}
                        </div>
                        <div class="stat-cell__value">
                          {{ item.stamp_count || 0 }}
                          <span class="stat-cell__unit"
                            >/ {{ stampsRequired }}</span
                          >
                        </div>
                        <v-progress-linear
                          :value="stampProgressPercent(item)"
                          height="4"
                          rounded
                          color="primary"
                          background-color="grey lighten-3"
                          class="stat-cell__progress"
                        />
                      </div>

                      <div class="stat-cell">
                        <div class="stat-cell__label">
                          {{ $t("history_count") }}
                        </div>
                        <div class="stat-cell__value">
                          {{ (item.history_buy_count || 0) | ToNumber }}
                        </div>
                      </div>

                      <div class="stat-cell">
                        <div class="stat-cell__label">
                          {{ $t("total") }}
                        </div>
                        <div class="stat-cell__value">
                          {{
                            (item.history_buy_sum_total_price ||
                              item.history_buy_sum_total_paid ||
                              0) | NumberFormat
                          }}
                        </div>
                      </div>
                    </div>

                    <!-- Right: Actions -->
                    <div class="customer-card__actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            class="action-btn action-btn-view"
                            :aria-label="$t('view')"
                            @click.stop="viewItem(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>fal fa-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("view") }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            class="action-btn action-btn-edit"
                            :aria-label="$t('edit')"
                            @click.stop="editItem(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>fal fa-edit</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("edit") }}</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            class="action-btn action-btn-delete"
                            :aria-label="$t('delete')"
                            @click.stop="deleteItem(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>fal fa-trash-alt</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("delete") }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card-text>
        </v-card>
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      title="customer"
      width="760px"
    >
      <div slot="activator">
        <div class="customer-view">
          <!-- Hero -->
          <div class="customer-view__hero">
            <v-avatar
              class="customer-view__avatar"
              size="64"
              color="primary"
              :aria-label="
                form.first_name && String(form.first_name).trim()
                  ? String(form.first_name).trim()
                  : $t('customer')
              "
            >
              <v-icon
                v-if="!(form.first_name && String(form.first_name).trim())"
                class="customer-view__avatar-icon white--text"
                size="32"
              >
                fal fa-user
              </v-icon>
              <span v-else class="avatar-initial white--text">{{
                initials(form.first_name)
              }}</span>
            </v-avatar>
            <div class="customer-info">
              <h3 class="customer-name">{{ form.first_name || "-" }}</h3>
              <div class="customer-info__row">
                <v-icon x-small class="mr-1">fal fa-phone</v-icon>
                {{ form.phone_no || "-" }}
                <span v-if="form.email" class="customer-info__sep">·</span>
                <template v-if="form.email">
                  <v-icon x-small class="mr-1">fal fa-envelope</v-icon>
                  {{ form.email }}
                </template>
              </div>
              <div class="customer-id">ID #{{ form.id || "-" }}</div>
            </div>
          </div>

          <!-- Tabs -->
          <v-tabs
            v-model="tab"
            grow
            class="customer-view__tabs"
            color="primary"
            slider-color="primary"
          >
            <v-tab>
              <v-icon left small>fal fa-id-badge</v-icon>
              {{ $t("overview") }}
            </v-tab>
            <v-tab v-if="isStampFeatureEnabled">
              <v-icon left small>fal fa-stamp</v-icon>
              {{ $t("stamp_card") }}
            </v-tab>
            <v-tab v-if="isStampFeatureEnabled">
              <v-icon left small>fal fa-history</v-icon>
              {{ $t("stamp_tab_history") }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="customer-view__tab-items">
            <!-- ============ OVERVIEW ============ -->
            <v-tab-item :transition="false" :reverse-transition="false">
              <div class="info-cards">
                <!-- Contact Information -->
                <div class="info-card">
                  <div class="card-header">
                    <v-icon class="card-icon">fal fa-mobile-alt</v-icon>
                    <span class="card-title">{{
                      $t("contact_information")
                    }}</span>
                  </div>
                  <div class="card-content">
                    <div class="info-row">
                      <span class="info-label">{{ $t("phone_no") }}</span>
                      <span class="info-value">{{ form.phone_no || "-" }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("email") }}</span>
                      <span class="info-value">{{
                        form.email || $t("not_provided")
                      }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("last_name") }}</span>
                      <span class="info-value">{{
                        form.last_name || $t("not_provided")
                      }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("created_at") }}</span>
                      <span class="info-value">{{
                        form.created_at | created_at
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Account Details -->
                <div class="info-card">
                  <div class="card-header">
                    <v-icon class="card-icon">fal fa-user</v-icon>
                    <span class="card-title">{{ $t("account_details") }}</span>
                  </div>
                  <div class="card-content">
                    <div class="info-row">
                      <span class="info-label">{{
                        $t("loyalty_points")
                      }}</span>
                      <span class="info-value"
                        >{{ (form.point || 0) | ToNumber }} pts</span
                      >
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("history_count") }}</span>
                      <span class="info-value">{{
                        (form.history_buy_count || 0) | ToNumber
                      }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("total") }}</span>
                      <span class="info-value">{{
                        (form.history_buy_sum_total_price ||
                          form.history_buy_sum_total_paid ||
                          0) | NumberFormat
                      }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t("status") }}</span>
                      <span class="info-value">
                        <v-chip x-small color="success" text-color="white">
                          {{ $t("active") }}
                        </v-chip>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Location Details -->
                <div class="info-card">
                  <div class="card-header">
                    <v-icon class="card-icon">fal fa-map-marker-alt</v-icon>
                    <span class="card-title">{{
                      $t("location_details")
                    }}</span>
                  </div>
                  <div class="card-content">
                    <div class="location-chips" v-if="form.village">
                      <v-chip small outlined class="location-chip">
                        {{ form.village.district.province.pr_name }}
                      </v-chip>
                      <v-chip small outlined class="location-chip">
                        {{ form.village.district.dr_name }}
                      </v-chip>
                      <v-chip small outlined class="location-chip">
                        {{ form.village.vill_name }}
                      </v-chip>
                    </div>
                    <div class="address-field">
                      <span class="info-label">{{ $t("address") }}</span>
                      <span class="info-value">{{
                        form.address || "-"
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Logistics Partner -->
                <div class="info-card" v-if="form.logistics">
                  <div class="card-header">
                    <v-icon class="card-icon">fal fa-truck</v-icon>
                    <span class="card-title">{{
                      $t("logistics_partner")
                    }}</span>
                  </div>
                  <div class="card-content">
                    <div class="logistics-info">
                      <div class="logistics-logo">
                        <v-avatar size="40">
                          <v-img :src="form.logistics.logo" />
                        </v-avatar>
                      </div>
                      <div class="logistics-details">
                        <div class="logistics-name">
                          {{ form.logistics.name }}
                        </div>
                        <v-chip x-small color="success" text-color="white">
                          {{ $t("active_partner") }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- ============ STAMPS ============ -->
            <v-tab-item
              v-if="isStampFeatureEnabled"
              :transition="false"
              :reverse-transition="false"
            >
              <div class="stamps-tab">
                <div
                  v-if="
                    !form.stamp_status ||
                    !form.stamp_status.stamp_feature_available
                  "
                  class="stamp-message-card"
                >
                  <v-icon color="amber darken-2" class="mb-2">
                    fal fa-info-circle
                  </v-icon>
                  {{ $t("stamp_retail_only") }}
                </div>
                <div
                  v-else-if="!form.stamp_status.is_active"
                  class="stamp-message-card"
                >
                  <v-icon color="amber darken-2" class="mb-2">
                    fal fa-pause-circle
                  </v-icon>
                  {{ $t("stamp_program_inactive") }}
                </div>
                <template v-else>
                  <StampCard :stamp-status="form.stamp_status" />

                  <!-- Earn rule -->
                  <div class="stamp-rule">
                    <v-icon small color="primary" class="mr-2">
                      fal fa-info-circle
                    </v-icon>
                    <span>
                      {{ form.stamp_status.stamps_per_purchase }}
                      {{ $t("stamp_progress").toLowerCase() }}
                      ·
                      {{ earnModeLabel(form.stamp_status.stamp_earn_mode) }}
                    </span>
                  </div>
                </template>
              </div>
            </v-tab-item>

            <!-- ============ HISTORY ============ -->
            <v-tab-item
              v-if="isStampFeatureEnabled"
              :transition="false"
              :reverse-transition="false"
            >
              <div class="history-tab">
                <div v-if="!form.id" class="stamp-empty-state">
                  <v-icon color="grey lighten-1" class="mb-2">
                    fal fa-user
                  </v-icon>
                  {{ $t("stamp_history_no_customer") }}
                </div>
                <div
                  v-else-if="stampHistoryLoading"
                  class="stamp-empty-state"
                >
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <v-alert
                  v-else-if="stampHistoryFailed"
                  type="error"
                  dense
                  outlined
                  class="mb-0"
                >
                  {{ $t("stamp_history_load_error") }}
                </v-alert>
                <div
                  v-else-if="!stampHistory.length"
                  class="stamp-empty-state"
                >
                  <v-icon color="grey lighten-1" class="mb-2">
                    fal fa-history
                  </v-icon>
                  {{ $t("stamp_history_empty") }}
                </div>
                <v-list
                  v-else
                  dense
                  two-line
                  class="stamp-history-list"
                >
                  <v-list-item
                    v-for="row in stampHistory"
                    :key="row.id"
                    class="stamp-history-item"
                  >
                    <v-list-item-icon class="stamp-history-icon">
                      <v-icon
                        small
                        :color="
                          row.type === 'redeemed'
                            ? 'orange darken-2'
                            : 'primary'
                        "
                      >
                        fal fa-stamp
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="stamp-history-title">
                        {{ historyRowTitle(row) }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="stamp-history-subtitle">
                        {{ formatHistoryDate(row.created_at) }}
                        <span v-if="row.ticket_id" class="ticket-tag">
                          · #{{ row.ticket_id }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </div>
    </app-custom-dialog>
    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      :title="titleStatus"
      :submitText="update ? 'update' : 'add_customer'"
      @submit="handleSubmit"
    >
      <div slot="activator">
        <CustomersIndex
          ref="customerForm"
          :key="update ? `edit-${form.id}` : 'new'"
          :customer-data="update ? form : null"
          @saved="handleSaved"
        />
      </div>
    </app-custom-dialog>
    <!-- AppLoading -->
    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { success, confirm } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import CustomersIndex from "@/components/customers/CustomerManager.vue";
import StampCard from "@/components/stamp/StampCard.vue";

export default {
  mixins: [myMixin],

  data: () => ({
    overlay: false,
    listLoading: true,
    listError: false,
    search: "",
    update: false,
    dialogAdd: false,
    dialog: false,
    loadingSummary: true,
    summaries: [],
    form: {},
    currentCustomer: null,
    options: {
      precision: 0,
    },
    tab: 0,
    stampHistory: [],
    stampHistoryLoading: false,
    stampHistoryFailed: false,
    stampConfig: null,
  }),
  async created() {
    await Promise.all([this.fetchItem(), this.fetchSummary()]);
    if (this.isRetailPosStore) {
      this.loadStampConfig();
    }
  },
  components: { CustomersIndex, StampCard },
  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        title: item.title,
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      customers: "index/customers",
      user: "auth/user",
    }),
    isRetailPosStore() {
      return this.user?.stores?.store_type_code === "POS";
    },

    isStampFeatureEnabled() {
      return !!(this.isRetailPosStore && this.stampConfig && this.stampConfig.is_active);
    },

    customerRows() {
      const customers = this.customers;
      if (Array.isArray(customers)) {
        return customers;
      }
      return Array.isArray(customers?.data) ? customers.data : [];
    },

    titleStatus() {
      return this.update ? "update" : "new_data";
    },
    memberSince() {
      if (!this.form.created_at) return "-";
      return this.$options.filters.created_at(this.form.created_at);
    },
    updatedDate() {
      if (!this.form.updated_at) return "-";
      return this.$options.filters.created_at(this.form.updated_at);
    },
    createdDate() {
      if (!this.form.created_at) return "-";
      return this.$options.filters.created_at(this.form.created_at);
    },
    stampsRequired() {
      return (
        this.form?.stamp_status?.stamps_required_to_redeem ||
        this.stampConfig?.stamps_required_to_redeem ||
        10
      );
    },
  },

  watch: {
    dialog(open) {
      if (!open) {
        this.tab = 0;
        this.stampHistory = [];
        this.stampHistoryFailed = false;
        this.stampHistoryLoading = false;
      }
    },
    tab(idx) {
      if (
        this.isStampFeatureEnabled &&
        idx === 2 &&
        this.form &&
        this.form.id
      ) {
        // Always refetch when the History tab opens so the list reflects
        // any sales/redeems that happened after the dialog was loaded.
        this.fetchStampHistory(this.form.id);
      }
    },
  },

  methods: {
    ...mapMutations({
      SET_CUSTOMER: "index/SET_CUSTOMER",
    }),
    async fetchItem() {
      this.listLoading = true;
      this.listError = false;
      try {
        const q = encodeURIComponent(this.search || "");
        const { data } = await this.$http.get(`customers_paginate?q=${q}`);
        if (data && data.status) {
          this.SET_CUSTOMER(data.data);
        } else {
          this.listError = true;
          this.SET_CUSTOMER({ data: [] });
        }
      } catch (e) {
        this.listError = true;
        this.SET_CUSTOMER({ data: [] });
      } finally {
        this.listLoading = false;
      }
    },

    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const { data } = await this.$http.get("get_summary_customers");
        if (data && data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log("error fetching customer summary", error);
      } finally {
        this.loadingSummary = false;
      }
    },

    async newItem() {
      this.update = false;
      this.dialogAdd = true;
    },
    async editItem(item) {
      const { data } = await this.$http.get(`customers_detail/${item.id}`);
      if (data.status) {
        this.update = true;
        this.form = data.data;
      }
      this.dialogAdd = true;
    },
    async deleteItem({ id }) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        const { data } = await this.$http.delete(`customers_delete/${id}`);
        if (data.status) {
          await Help.clearForm(this.form, this);
          const rows = this.customerRows;
          const idx = rows.findIndex((item) => item.id === id);
          if (idx >= 0 && Array.isArray(this.customers?.data)) {
            this.customers.data.splice(idx, 1);
          }
          await this.fetchSummary();
          await success(this, data.message);
        }
      }
    },
    async viewItem(item) {
      this.tab = 0;
      this.stampHistory = [];
      this.stampHistoryFailed = false;
      this.stampHistoryLoading = false;
      const { data } = await this.$http.get(`customers_detail/${item.id}`);
      if (!data || !data.status) return;

      this.form = data.data;
      this.dialog = true;

      // Backend now eagerly attaches the most recent stamp logs as
      // form.stamp_history when the program is active. Prefer that payload
      // and only fall back to the dedicated endpoint when it's missing.
      if (Array.isArray(this.form.stamp_history)) {
        this.stampHistory = this.form.stamp_history;
      } else if (this.isStampFeatureEnabled && this.form.id) {
        this.fetchStampHistory(this.form.id);
      }
    },
    async fetchStampHistory(id) {
      if (!id || this.stampHistoryLoading) return;
      this.stampHistoryLoading = true;
      this.stampHistoryFailed = false;
      try {
        const { data } = await this.$http.get(
          `customers/${id}/stamp_history`,
          { params: { limit: 30 } }
        );
        if (data && data.status) {
          this.stampHistory = Array.isArray(data.data) ? data.data : [];
        } else {
          this.stampHistory = [];
          this.stampHistoryFailed = true;
        }
      } catch (e) {
        this.stampHistory = [];
        this.stampHistoryFailed = true;
      } finally {
        this.stampHistoryLoading = false;
      }
    },
    historyRowTitle(row) {
      const n = Math.abs(Number(row.stamps_changed) || 0);
      if (row.type === "redeemed") {
        return this.$t("stamp_log_redeemed", { n });
      }
      return this.$t("stamp_log_earned", { n });
    },
    formatHistoryDate(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
      } catch (e) {
        return String(iso);
      }
    },
    earnModeLabel(mode) {
      return mode === "ticket"
        ? this.$t("stamp_earn_mode_ticket")
        : this.$t("stamp_earn_mode_item_qty");
    },
    stampProgressPercent(item) {
      const required = this.stampsRequired || 10;
      const current = Number(item && item.stamp_count) || 0;
      if (required <= 0) return 0;
      return Math.min(100, Math.round((current / required) * 100));
    },
    async loadStampConfig() {
      try {
        const { data } = await this.$http.get("stamp_config");
        if (data && data.status && data.data) {
          this.stampConfig = data.data;
        }
      } catch (e) {
        this.stampConfig = null;
      }
    },
    async handleSaved() {
      this.dialogAdd = false;
      await this.fetchItem();
      await this.fetchSummary();
      await success(this, this.$t(this.update ? "update" : "create"));
    },
    async handleSubmit() {
      if (this.$refs.customerForm) {
        await this.$refs.customerForm.saveCustomer();
      }
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0]?.[0] || "?";
      const last = (parts[1] && parts[1][0]) || "";
      return (first + last).toUpperCase();
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard?.writeText(String(text)).catch(() => {});
    },
    dial(phone) {
      if (!phone) return;
      window.open(`tel:${phone}`, "_self");
    },
    editFromView() {
      if (!this.form?.id) return;
      this.editItem(this.form);
    },
    async deleteFromView() {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        const { data } = await this.$http.delete(
          `customers_delete/${this.form.id}`
        );
        if (data.status) {
          await Help.clearForm(this.form, this);
          const rows = this.customerRows;
          const idx = rows.findIndex((item) => item.id === this.form.id);
          if (idx >= 0 && Array.isArray(this.customers?.data)) {
            this.customers.data.splice(idx, 1);
          }
          this.dialog = false;
          await success(this, data.message);
        }
      }
    },
  },
};
</script>

<style scoped>
/* =====================================================================
 * Design tokens (scoped fallback — works even when landing-light is not
 * imported on this page). Keeps the page consistent with the rest of the
 * app while avoiding hard-coded brand colors.
 * ===================================================================== */
.container-xl {
  --c-primary: var(--color-primary, #5b39c9);
  --c-primary-soft: var(--color-primary-10, rgba(91, 57, 201, 0.1));
  --c-primary-ring: var(--color-primary-20, rgba(91, 57, 201, 0.2));
  --c-amber: #f59e0b;
  --c-text: var(--text-primary, #0f172a);
  --c-muted: var(--text-muted, #64748b);
  --c-soft: var(--text-secondary, #475569);
  --c-border: #e2e8f0;
  --c-border-strong: #cbd5e1;
  --c-surface: var(--bg-primary, #ffffff);
  --c-surface-2: #f8fafc;
}

.theme--dark .container-xl {
  --c-text: #f8fafc;
  --c-muted: #94a3b8;
  --c-soft: #cbd5e1;
  --c-border: #334155;
  --c-border-strong: #475569;
  --c-surface: #1e293b;
  --c-surface-2: #0f172a;
}

/* Main Card */
.main-customers-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: none !important;
}

.theme--dark .main-customers-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background: #1e1e2d !important;
}

/* =====================================================================
 * List card (Styled like .staff-row-card on /manage-users: 16px radius, 1px border, zero shadow)
 * ===================================================================== */
.customer-card {
  cursor: pointer;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  transition: border-color 0.15s ease, transform 0.12s ease;
  position: relative;
  overflow: hidden;
}

.theme--dark .customer-card {
  border-color: #334155 !important;
  background: #262638 !important;
}

.customer-card:hover {
  border-color: #5b39c9 !important;
  box-shadow: none !important;
  transform: translateY(-1px);
}

.theme--dark .customer-card:hover {
  border-color: #8b6ff0 !important;
}

.customer-card:active {
  transform: scale(0.995);
}

.customer-card:focus-visible,
.customer-card:focus-within {
  outline: 2px solid var(--c-primary-ring);
  outline-offset: 2px;
}

.customer-card__inner {
  display: grid;
  grid-template-columns: minmax(220px, 1.3fr) minmax(0, 2fr) auto;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
}

/* identity */
.customer-card__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.customer-card__avatar {
  flex-shrink: 0;
  box-shadow: none !important;
}

.customer-card__identity-text {
  min-width: 0;
}

.customer-card__name {
  font-size: 15px;
  font-weight: 700;
  color: var(--c-text);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s ease;
}

.customer-card:hover .customer-card__name {
  color: #5b39c9;
}

.theme--dark .customer-card:hover .customer-card__name {
  color: #c4b5fd;
}

.customer-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 4px;
  font-size: 12px;
  color: var(--c-muted);
}

.customer-card__meta-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.customer-card__meta-id {
  font-weight: 600;
  color: var(--c-soft);
  background: var(--c-surface-2);
  padding: 1px 8px;
  border-radius: 6px;
}

/* stat strip */
.customer-card__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 12px;
  align-items: center;
}

.stat-cell {
  min-width: 0;
}

.stat-cell__label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--c-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-cell__value {
  font-size: 16px;
  font-weight: 700;
  color: var(--c-text);
  margin-top: 2px;
  line-height: 1.2;
}

.stat-cell__unit {
  font-size: 11px;
  font-weight: 500;
  color: var(--c-muted);
  margin-left: 2px;
}

.stat-cell__progress {
  margin-top: 6px;
  border-radius: 999px;
}

/* actions */
.customer-card__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  width: 36px;
  height: 36px;
  transition: background-color 200ms ease, color 200ms ease;
  border-radius: 10px !important;
}

.action-btn:focus-visible {
  outline: 3px solid var(--c-primary-ring);
  outline-offset: 2px;
}

.action-btn-view {
  color: var(--c-primary) !important;
}
.action-btn-view:hover {
  background-color: var(--c-primary-soft) !important;
}

.action-btn-edit {
  color: #16a34a !important;
}
.action-btn-edit:hover {
  background-color: rgba(22, 163, 74, 0.1) !important;
}

.action-btn-delete {
  color: #dc2626 !important;
}
.action-btn-delete:hover {
  background-color: rgba(220, 38, 38, 0.1) !important;
}

/* =====================================================================
 * Detail dialog (tabs)
 * ===================================================================== */
.customer-view {
  background: var(--c-surface, #ffffff);
}

.customer-view__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--c-border);
  background: var(--c-surface-2);
}

/* Primary circle: Vuetify `color="primary"` */
.customer-view__avatar {
  flex-shrink: 0;
  font-size: 24px;
  font-weight: 700;
  box-shadow: none !important;
}

.customer-view__avatar ::v-deep .v-icon {
  opacity: 0.95;
}

.avatar-initial {
  letter-spacing: 0.02em;
  line-height: 1;
}

.customer-info {
  min-width: 0;
}

.customer-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--c-text);
  margin: 0 0 4px;
  line-height: 1.25;
}

.customer-info__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: var(--c-muted);
  font-size: 13px;
}

.customer-info__sep {
  margin: 0 4px;
}

.customer-id {
  font-size: 12px;
  color: var(--c-muted);
  font-weight: 500;
  margin-top: 4px;
}

/* tabs */
.customer-view__tabs {
  border-bottom: 1px solid var(--c-border);
  padding: 0 12px;
}

.customer-view__tabs ::v-deep .v-tab {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  font-size: 13px;
  min-height: 48px;
  color: var(--c-muted);
}

.customer-view__tabs ::v-deep .v-tab--active {
  color: var(--c-primary);
}

.customer-view__tabs ::v-deep .v-tab:focus-visible {
  outline: 3px solid var(--c-primary-ring);
  outline-offset: -3px;
}

.customer-view__tab-items {
  background: transparent;
}

/* info-cards inside Overview */
.info-cards {
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.info-card {
  background: var(--c-surface, #ffffff);
  border-radius: 16px;
  border: 1px solid var(--c-border);
  box-shadow: none !important;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--c-surface-2);
  border-bottom: 1px solid var(--c-border);
}

.card-icon {
  color: var(--c-soft);
  font-size: 14px !important;
}

.card-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-text);
}

.card-content {
  padding: 14px 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--c-border);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  color: var(--c-muted);
  font-weight: 500;
}

.info-value {
  font-size: 13px;
  color: var(--c-text);
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

/* location chips */
.location-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.location-chip {
  font-size: 11px;
  font-weight: 500;
  border-color: rgba(91, 57, 201, 0.25) !important;
  color: var(--c-primary) !important;
  background-color: var(--c-primary-soft) !important;
}

.btn-primary-brand {
  background-color: var(--c-primary) !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
}

.btn-primary-brand:hover {
  background-color: #4f2fb9 !important;
}

.address-field {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
}

/* logistics */
.logistics-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logistics-logo {
  flex-shrink: 0;
}

.logistics-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.logistics-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-text);
}

/* =====================================================================
 * Stamps tab
 * ===================================================================== */
.stamps-tab {
  padding: 20px;
}

.stamp-card {
  position: relative;
  overflow: hidden;
  padding: 18px;
  border: 1px solid var(--c-border);
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    rgba(91, 57, 201, 0.04) 60%,
    rgba(245, 158, 11, 0.06) 100%
  );
}

.stamp-card::before {
  content: "";
  position: absolute;
  top: -36px;
  right: -32px;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.16);
  pointer-events: none;
}

.stamp-card__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.stamp-card__eyebrow {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--c-muted);
}

.stamp-card__count {
  font-size: 28px;
  font-weight: 800;
  color: var(--c-text);
  line-height: 1.1;
  margin-top: 2px;
}

.stamp-card__chip {
  text-transform: none;
}

.stamp-progress {
  margin: 4px 0 14px;
}

.stamp-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(34px, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.stamp-slot {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  border: 1.5px dashed var(--c-border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: transparent;
  transition: background 200ms ease, border-color 200ms ease;
}

.stamp-slot.filled {
  background: var(--c-primary);
  border-color: var(--c-primary);
  border-style: solid;
}

.stamp-card__footer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--c-soft);
  font-weight: 500;
}

.stamp-rule {
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding: 12px 14px;
  background: var(--c-primary-soft);
  border: 1px solid var(--c-primary-ring);
  border-radius: 10px;
  font-size: 13px;
  color: var(--c-text);
}

.stamp-message-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 20px;
  border: 1px dashed var(--c-border-strong);
  border-radius: 12px;
  color: var(--c-soft);
  font-size: 13px;
  background: var(--c-surface-2);
}

/* =====================================================================
 * History tab
 * ===================================================================== */
.history-tab {
  padding: 16px 20px 20px;
}

.stamp-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  color: var(--c-muted);
  font-size: 13px;
}

.stamp-history-list {
  background: transparent !important;
  padding: 0 !important;
}

.stamp-history-item {
  border-bottom: 1px solid var(--c-border);
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.stamp-history-item:last-child {
  border-bottom: none;
}

.stamp-history-icon {
  margin-right: 12px !important;
}

.stamp-history-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-text);
}

.stamp-history-subtitle {
  font-size: 12px;
  color: var(--c-muted);
}

.ticket-tag {
  font-weight: 500;
  color: var(--c-soft);
}

/* =====================================================================
 * Responsive
 * ===================================================================== */
@media (max-width: 960px) {
  .customer-card__inner {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .customer-card__actions {
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .customer-view__hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
  }

  .info-cards {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .stamps-tab,
  .history-tab {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .customer-card__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
